import { computed, markRaw, ref } from 'vue'

import {
  FastLinkCloseResponse,
  FastLinkErrorResponse,
  PlaidOnExitResponseError,
  TaskType,
} from '@types'

import { YODLEE_ONCLOSE_STATUSES } from './utils/const'

import { appendScript, handleCatchedError, waitForNode } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import { useLinkedDataStore } from '@/store/linkedData'
import { useLinkedDataConnectorsStore } from '@/store/linkedData/connectors'
import { useTasksStore } from '@/store/tasks'
import { useModalsStore } from '@/store/modals'

import { LinkedDataConnectorStatus } from '@/views/LinkedData/utils/enums'

import AddAccountYodlee from '@/views/LinkedData/Connectors/components/AddAccountYodlee.vue'

const loading = ref(false)
const innerSyncing = ref(false)

export const useLinkedDataConnector = (callback?: (flag: boolean) => void) => {
  const linkedDataStore = useLinkedDataStore()
  const linkedDataConnectorsStore = useLinkedDataConnectorsStore()
  const tasksStore = useTasksStore()
  const modalsStore = useModalsStore()

  const { error } = useNotifications()

  const plaidToken = ref()
  const accessInfo = ref()

  const outerSyncing = computed(() => {
    return !!tasksStore.getProcessing.find(
      task => task.type === TaskType.SYNC_HOLDING_ACCOUNT,
    )
  })

  const mapped = computed(() => {
    return !!tasksStore.getAccountsMapped.find(
      task => task.type === TaskType.SYNC_HOLDING_ACCOUNT,
    )
  })

  const syncing = computed(() => {
    return innerSyncing.value || outerSyncing.value
  })

  const syncingConnectors = computed(() => {
    return tasksStore.getProcessing
      .filter(task => task.type === TaskType.SYNC_HOLDING_ACCOUNT)
      .map(task => task.linked_type?.toLowerCase())
  })

  const issueConnectorsCount = computed(() => {
    const connectors = linkedDataConnectorsStore.getList

    if (!connectors || connectors.length === 0) return 0

    return connectors.filter(connector => [
      LinkedDataConnectorStatus.ERROR,
      LinkedDataConnectorStatus.LOGIN_REQUIRED,
    ].includes(connector.status)).length
  })

  const reloadToken = async () => {
    loading.value = true
    accessInfo.value = await linkedDataStore.getAccess()
    loading.value = false
  }

  const fetchPlaidTasks = async (token: string) => {
    innerSyncing.value = true
    try {
      const result = await linkedDataStore.plaid(token)
      tasksStore.commit([result])
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      innerSyncing.value = false
    }
  }

  const fetchYodleeTasks = async () => {
    innerSyncing.value = true
    try {
      const result = await linkedDataStore.yodlee()
      tasksStore.commit([result])
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      innerSyncing.value = false
    }
  }

  const fetchArchTasks = async (clientId: string, clientSecret: string) => {
    innerSyncing.value = true
    try {
      const result = await linkedDataStore.arch(clientId, clientSecret)
      tasksStore.commit([result])
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      innerSyncing.value = false
    }
  }

  const openPlaid = () => {
    if (!plaidToken.value) return
    ;(window as Window & typeof globalThis & { Plaid: any }).Plaid.create({
      token: plaidToken.value,
      onLoad: () => {
        callback?.(false)
      },
      onSuccess: async (public_token: string) => {
        fetchPlaidTasks(public_token)
      },
      onExit: function (err: PlaidOnExitResponseError | null) {
        if (err) {
          error({
            message: err.display_message,
          })
        }
      },
    }).open()
  }

  const openYodleeFastLink = async (institutionId?: string) => {
    if (!accessInfo.value) return
    const modalInstance = modalsStore.init('add-account-yodlee', markRaw(AddAccountYodlee))
    modalInstance?.open(modalsStore.getZIndex())
    const container = await waitForNode('.add-account-yodlee__container')
    const containerId = container?.getAttribute('id')
    ;(window as Window & typeof globalThis & { fastlink: any }).fastlink.open(
      {
        fastLinkURL: accessInfo.value.url,
        accessToken: `Bearer ${accessInfo.value.token}`,
        forceIframe: true, // Force iframe rendering on mobile devices - https://developer.yodlee.com/resources/yodlee/fastlink-4/docs/documentation
        params: {
          configName: 'Aggregation',
          ...(institutionId && {
            flow: 'add',
            providerId: institutionId,
          }),
        },
        onError: async (data: FastLinkErrorResponse) => {
          error(data)
        },
        onClose: (data: FastLinkCloseResponse) => {
          modalInstance?.close()
          if (!data.status && data.sites?.length) {
            fetchYodleeTasks()
          }
          if (data.status === YODLEE_ONCLOSE_STATUSES.USER_CLOSE_ACTION) {
            callback?.(true)
          }
        },
      },
      containerId,
    )
  }

  const plaid = async (connectorId?: string, routingNumber?: string | null) => {
    try {
      const data = await linkedDataConnectorsStore.getPlaidToken(
        connectorId,
        routingNumber,
      )
      plaidToken.value = data?.link_token
    } catch (e) {
      handleCatchedError(e as string)
      error({
        message: 'Error while getting Plaid token',
      })
      return
    }
    appendScript(
      'https://cdn.plaid.com/link/v2/stable/link-initialize.js',
      'plaid-script',
    ).then(openPlaid)
  }

  const yodlee = async (id?: string) => {
    await reloadToken()
    appendScript(
      'https://cdn.yodlee.com/fastlink/v4/initialize.js',
      'yodlee-fastlink',
    ).then(() => {
      openYodleeFastLink(id)
      callback?.(false)
    })
  }

  const arch = fetchArchTasks

  const sync = async () => {
    if (syncing.value) return

    innerSyncing.value = true
    try {
      const result = await linkedDataStore.sync()
      tasksStore.commit(result)
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      innerSyncing.value = false
    }
  }

  const resync = async (connectorId: string) => {
    if (syncing.value) return

    innerSyncing.value = true
    try {
      const result = await linkedDataStore.resync(connectorId)
      tasksStore.commit([result])
    } catch (e) {
      handleCatchedError(e as string)
    } finally {
      innerSyncing.value = false
    }
  }

  return {
    loading,
    syncing,
    mapped,

    syncingConnectors,
    issueConnectorsCount,

    plaid,
    yodlee,
    arch,

    sync,
    resync,
  }
}
